import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import moment from "moment";
import {
  FiCheck,
  FiCopy,
  FiCheckCircle,
  FiArrowRight,
  FiPlus,
  FiGrid,
  FiPhoneCall,
  FiCamera,
  FiShield,
  FiTrendingUp,
  FiHome,
  FiMapPin,
  FiLayers,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import Header from "../components/layouts/Header";
import Footer from "../components/layouts/Footer";
import {
  setPostPropertyStepsStatus,
  pageStatusChange,
} from "../redux/property/PropertySlice";
import "./PostSuccessNew.css";

const PostSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentPost } = useSelector((store) => store.Property);
  const [copied, setCopied] = useState(false);

  // Retrieve posted property from redux or localStorage or default fallback
  const getPropertyData = () => {
    if (currentPost && typeof currentPost === "object" && currentPost.propertyID) {
      return currentPost;
    }
    try {
      const stored = localStorage.getItem("lastPostedProperty");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed) return parsed;
      }
    } catch (e) {
      // fallback gracefully
    }
    return {
      propertyID: "MGH-842190",
      ExpiryDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
      postUserName: "Owner",
      postUserMobile: "9063483711",
      city: "Hyderabad",
      locality: "Financial District",
      apartment: "My Golden Residency",
      subcatName: "Flat/Apartment",
      catName: "Sell",
      expectedPrice: "12500000",
      carpetArea: "1625",
      noofBedrooms: "3",
      noofBathrooms: "3",
      percentageCompletion: 92,
    };
  };

  const property = getPropertyData();

  const propertyId = property.propertyID || property._id || "MGH-842190";
  const expiryDate = property.ExpiryDate
    ? moment(property.ExpiryDate).format("DD MMMM YYYY")
    : moment().add(90, "days").format("DD MMMM YYYY");

  // Format Price
  const formatPrice = (price) => {
    if (!price) return "Price on Request";
    const num = Number(price);
    if (isNaN(num)) return `₹ ${price}`;
    if (num >= 10000000) {
      return `₹ ${(num / 10000000).toFixed(2)} Cr`;
    }
    if (num >= 100000) {
      return `₹ ${(num / 100000).toFixed(2)} Lac`;
    }
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  // Copy Property ID
  const handleCopyId = () => {
    navigator.clipboard.writeText(propertyId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // WhatsApp Share
  const handleShareWhatsapp = () => {
    const text = `Hey, check out this property listing on EwRealty: ${property.subcatName || "Property"} in ${property.locality || "Hyderabad"} (Property ID: ${propertyId}) at ${window.location.origin}/property-info/${propertyId}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handlePostAnother = () => {
    dispatch(setPostPropertyStepsStatus(0));
    navigate("/postPropertyHome");
  };

  const handleGoDashboard = () => {
    dispatch(pageStatusChange(4)); // Manage Listings tab
    navigate("/dashboard");
  };

  const completionPercent = Number(property.percentageCompletion) || 92;

  return (
    <div className="mgh-success-page">
      <Header />

      <main className="mgh-success-container">
        {/* ================= 1. Hero Celebration Card ================= */}
        <div className="mgh-success-hero-card">
          <div className="mgh-success-badge-icon-wrap">
            <div className="mgh-success-badge-ring" />
            <div className="mgh-success-badge-circle">
              <FiCheck />
            </div>
          </div>

          <h1 className="mgh-success-title">
            Congratulations! Your Property is <span>Successfully Posted</span>
          </h1>

          <p className="mgh-success-subtitle">
            Your property has been submitted and is currently in fast verification.
            It will be published live and start receiving genuine buyer inquiries within 2 to 4 hours.
          </p>

          {/* Meta Info Bar */}
          <div className="mgh-success-meta-row">
            <div className="mgh-meta-pill-item">
              <span>Property ID:</span>
              <strong>{propertyId}</strong>
              <button
                type="button"
                className="mgh-meta-copy-btn"
                onClick={handleCopyId}
                title="Copy Property ID"
              >
                {copied ? <FiCheckCircle size={13} /> : <FiCopy size={13} />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>

            <div className="mgh-meta-pill-divider" />

            <div className="mgh-meta-pill-item">
              <span>Valid Until:</span>
              <strong>{expiryDate}</strong>
            </div>

            <div className="mgh-meta-pill-divider" />

            <div className="mgh-meta-status-badge">
              <span className="mgh-pulse-dot" />
              <span>Active & Under Review</span>
            </div>
          </div>

          {/* ================= 2. Property Overview Summary ================= */}
          <div className="mgh-success-prop-box">
            <div className="mgh-prop-box-header">
              <div className="mgh-prop-box-title">
                {property.noofBedrooms ? `${property.noofBedrooms} BHK ` : ""}
                {property.subcatName || "Property"} for {property.catName || "Sale"}
                {property.apartment ? ` in ${property.apartment}` : ""}
              </div>
              <div className="mgh-prop-box-price">
                {formatPrice(property.expectedPrice)}
              </div>
            </div>

            <div className="mgh-prop-box-specs">
              <span className="mgh-spec-chip">
                <FiMapPin size={14} style={{ color: "#ea580c" }} />
                {property.locality || "Hyderabad"}, {property.city || "Telangana"}
              </span>
              {property.carpetArea && (
                <span className="mgh-spec-chip">
                  <FiLayers size={14} style={{ color: "#ea580c" }} />
                  {property.carpetArea} sq.ft
                </span>
              )}
              {property.noofBedrooms && (
                <span className="mgh-spec-chip">
                  <FiHome size={14} style={{ color: "#ea580c" }} />
                  {property.noofBedrooms} Bedrooms
                </span>
              )}
            </div>
          </div>

          {/* ================= 3. Primary Actions Wrap ================= */}
          <div className="mgh-success-actions-wrap">
            <button
              type="button"
              className="mgh-btn-primary-orange"
              onClick={handleGoDashboard}
            >
              <FiGrid size={16} />
              <span>Manage in Dashboard</span>
            </button>

            <button
              type="button"
              className="mgh-btn-outline-dark"
              onClick={handlePostAnother}
            >
              <FiPlus size={16} />
              <span>Post Another Property</span>
            </button>

            <button
              type="button"
              className="mgh-btn-whatsapp-share"
              onClick={handleShareWhatsapp}
            >
              <FaWhatsapp size={17} />
              <span>Share on WhatsApp</span>
            </button>
          </div>
        </div>

        {/* ================= 4. Property Completeness Score Booster ================= */}
        <div className="mgh-score-booster-card">
          <div className="mgh-score-left">
            <div className="mgh-score-badge">
              <FiTrendingUp size={13} />
              <span>PRO SELLER TIP</span>
            </div>
            <h3>Why Miss Out on a 100% Property Score?</h3>
            <p>
              Properties with complete details & photos receive up to <strong>3X more inquiries</strong>.
              Add floor plans or ownership documents anytime in your dashboard to boost your score!
            </p>
            <div className="mgh-score-progress-bar-wrap">
              <div
                className="mgh-score-progress-fill"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>

          <div className="mgh-score-right">
            <div className="mgh-score-percent">{completionPercent}%</div>
            <div className="mgh-score-label">Property Score</div>
          </div>
        </div>

        {/* ================= 5. Key Benefits of Verification Section ================= */}
        <section className="mgh-benefits-section">
          <div className="mgh-benefits-header">
            <h2>Get Your Property Verified for FREE</h2>
            <p>Stand out in search results and gain instant buyer trust</p>
          </div>

          <div className="mgh-benefits-grid">
            <div className="mgh-benefit-card">
              <div className="mgh-benefit-icon-wrap">
                <FiCamera />
              </div>
              <h4>FREE HD Photoshoot</h4>
              <p>Professional high-res photography to make your listing look stunning.</p>
            </div>

            <div className="mgh-benefit-card">
              <div className="mgh-benefit-icon-wrap">
                <FiShield />
              </div>
              <h4>Verified Seller Tag</h4>
              <p>Earn the green verified badge to gain 100% trust from genuine buyers.</p>
            </div>

            <div className="mgh-benefit-card">
              <div className="mgh-benefit-icon-wrap">
                <FiTrendingUp />
              </div>
              <h4>5X Higher Responses</h4>
              <p>Verified properties rank higher and get up to 5x more lead inquiries.</p>
            </div>
          </div>
        </section>

        {/* ================= 6. Instant Help & Verification Banner ================= */}
        <div className="mgh-success-help-banner">
          <div className="mgh-help-left">
            <div className="mgh-help-icon">
              <FiPhoneCall />
            </div>
            <div className="mgh-help-info">
              <h4>Need Immediate Verification or Assistance?</h4>
              <p>Our dedicated seller support team is available from 9:00 AM to 8:00 PM IST.</p>
            </div>
          </div>

          <div className="mgh-help-right">
            <a
              href="tel:+919063483711"
              className="mgh-help-call-btn"
            >
              <FiPhoneCall size={16} />
              <span>Call Helpline: +91 90634 83711</span>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PostSuccess;
